import { createContext } from 'react';
import type { Permission } from '@moto/shared';

import type { CurrentUser, LoginRequest } from '@/api/auth';

/**
 * The auth context, kept in its own module.
 *
 * Contexts live apart from the components that provide them so that a provider
 * file exports nothing but components. That is what keeps React Fast Refresh
 * able to hot-swap `AuthProvider` during development instead of remounting the
 * whole tree — and remounting the tree means losing the signed-in state you were
 * in the middle of debugging.
 */

/**
 * Three states, not a boolean.
 *
 * `loading` is a real state with a real screen: while `GET /auth/me` is in
 * flight the app knows nothing, and collapsing that into "unauthenticated"
 * would flash the login form at every user on every hard refresh.
 */
export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  status: AuthStatus;
  /** `null` unless `status === 'authenticated'`. */
  user: CurrentUser | null;
  /**
   * A `Set` rather than the array from the wire: permission checks run on every
   * render of every toolbar, and membership on a Set is constant-time.
   */
  permissions: ReadonlySet<Permission>;
  /** Branches the user may act in. Meaningless when `hasGlobalScope` is true. */
  branchIds: readonly string[];
  warehouseIds: readonly string[];
  hasGlobalScope: boolean;
  /** The user must change their password before the app is usable. */
  mustChangePassword: boolean;

  login: (credentials: LoginRequest) => Promise<CurrentUser>;
  isLoggingIn: boolean;
  logout: () => Promise<void>;
  isLoggingOut: boolean;
  /** Re-reads `GET /auth/me`, e.g. after an admin changed the user's role. */
  refresh: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
